/**
 * Centralized API client for the Venture Readiness Portal.
 *
 * All backend calls go through the `api` axios instance which is pre-configured
 * with the base URL and cookie credentials.  Every exported function maps 1-to-1
 * to a backend endpoint.
 *
 * USE_MOCK: set to true during local development without a running backend.
 * When true, functions return hardcoded data after a simulated network delay.
 * Must be false for any deployed environment.
 *
 * Error handling: login, submitApplication, and uploadDataRoomItem translate
 * HTTP error codes into user-friendly messages.  All other functions let errors
 * propagate so callers can handle them as needed.
 */
import axios from 'axios';

// Flip to true to run without a backend (mock data returned instead of API calls).
const USE_MOCK = false;

const api = axios.create({ baseURL: '/api/vr', withCredentials: true });

// Simulates network latency in mock mode so UI loading states are visible during dev.
function delay(ms = 400) {
  return new Promise((res) => setTimeout(res, ms));
}

// ─── Auth ──────────────────────────────────────────────────────────────────────

export async function getMe() {
  if (USE_MOCK) { await delay(150); return null; }
  const { data } = await api.get('/auth/me');
  return data;
}

export async function login(email, password) {
  if (USE_MOCK) { await delay(500); throw new Error('Mock mode: set USE_MOCK=false'); }
  try {
    const { data } = await api.post('/auth/login', { email, password });
    if (data.mfa_required) return { mfa_required: true, mfa_token: data.mfa_token };
    return data.user;
  } catch (err) {
    const status = err.response?.status;
    const detail = err.response?.data?.detail || '';
    if (status === 429) throw new Error(detail || 'Account temporarily locked. Try again in a few minutes.');
    // Generic message for 401 — do not disclose whether the email has an account.
    if (status === 401) throw new Error(detail || 'Invalid email or password.');
    if (status === 400 && detail.toLowerCase().includes('pending')) {
      throw new Error('Your application is still under review. We\'ll email you when it\'s approved.');
    }
    if (status === 400 && detail.toLowerCase().includes('rejected')) {
      throw new Error('Your application was not approved. Please contact the program for more information.');
    }
    if (!err.response) throw new Error('Unable to reach the server. Please check your connection and try again.');
    throw new Error(detail || 'Something went wrong. Please try again.');
  }
}

export async function mfaLogin(mfa_token, code) {
  if (USE_MOCK) { await delay(300); throw new Error('Mock mode'); }
  try {
    const { data } = await api.post('/auth/mfa/login', { mfa_token, code });
    return data.user;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    throw new Error(detail || 'Invalid code. Please try again.');
  }
}

export async function mfaSetup() {
  const { data } = await api.post('/auth/mfa/setup');
  return data;
}

export async function mfaConfirm(code) {
  const { data } = await api.post('/auth/mfa/confirm', { code });
  return data;
}

export async function mfaDisable(code) {
  const { data } = await api.post('/auth/mfa/disable', { code });
  return data;
}

export async function logout() {
  if (USE_MOCK) { await delay(150); return; }
  await api.post('/auth/logout');
}

// ─── Application ───────────────────────────────────────────────────────────────

export async function submitApplication(fields, pitchDeckFile = null) {
  if (USE_MOCK) {
    await delay(800);
    return { message: 'Application submitted successfully' };
  }
  try {
    const form = new FormData();
    Object.entries(fields).forEach(([k, v]) => form.append(k, v ?? ''));
    if (pitchDeckFile) form.append('pitch_deck', pitchDeckFile);
    const { data } = await api.post('/application', form);
    return data;
  } catch (err) {
    const status = err.response?.status;
    const detail = err.response?.data?.detail || '';
    if (status === 400 && detail.toLowerCase().includes('already')) {
      throw new Error('An account with this email already exists. Try signing in instead.');
    }
    if (!err.response) throw new Error('Unable to reach the server. Please check your connection and try again.');
    throw new Error(detail || 'Something went wrong. Please try again.');
  }
}

export async function uploadPitchDeck(file) {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await api.post('/files/pitch-deck', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    if (!err.response) throw new Error('Unable to reach the server. Check your connection and try again.');
    throw new Error(detail || 'Upload failed. Please try again.');
  }
}

export async function getApplicationStatus(email) {
  if (USE_MOCK) {
    await delay(300);
    return { status: 'pending' };
  }
  const { data } = await api.get('/application/status', { params: { email } });
  return data;
}

// ─── CEG ───────────────────────────────────────────────────────────────────────

export async function getCEGOverview() {
  if (USE_MOCK) {
    await delay(400);
    return {
      stats: {
        active_founders: 6,
        active_eirs: 3,
        avg_data_room_pct: 58,
        pending_applications: 2,
      },
      founders: [
        { id: 1,  first_name: 'Alex',    last_name: 'Rivera',  company: 'NovaBuild',    stage: 'revenue',    data_room_pct: 83, eir_id: 2, eir_name: 'Jane Smith',   last_active: '2026-05-28', has_fundraising: true  },
        { id: 5,  first_name: 'Priya',   last_name: 'Kapoor',  company: 'GreenTrace',   stage: 'pilot',      data_room_pct: 67, eir_id: 2, eir_name: 'Jane Smith',   last_active: '2026-05-27', has_fundraising: true  },
        { id: 6,  first_name: 'Marcus',  last_name: 'Dell',    company: 'FlowOps',      stage: 'prototype',  data_room_pct: 40, eir_id: 7, eir_name: 'Carlos Vega',  last_active: '2026-05-25', has_fundraising: false },
        { id: 8,  first_name: 'Simone',  last_name: 'Carter',  company: 'HealthLedger', stage: 'revenue',    data_room_pct: 92, eir_id: 7, eir_name: 'Carlos Vega',  last_active: '2026-05-29', has_fundraising: true  },
        { id: 9,  first_name: 'Leo',     last_name: 'Nguyen',  company: 'TalentOS',     stage: 'idea',       data_room_pct: 18, eir_id: 11, eir_name: 'Rita Owens',  last_active: '2026-05-20', has_fundraising: false },
        { id: 10, first_name: 'Danielle',last_name: 'Marsh',   company: 'FarmLink',     stage: 'pilot',      data_room_pct: 55, eir_id: 11, eir_name: 'Rita Owens',  last_active: '2026-05-26', has_fundraising: true  },
      ],
      eirs: [
        { id: 2,  first_name: 'Jane',   last_name: 'Smith', email: 'jane.smith@example.com',   founder_count: 2 },
        { id: 7,  first_name: 'Carlos', last_name: 'Vega',  email: 'carlos.vega@example.com',  founder_count: 2 },
        { id: 11, first_name: 'Rita',   last_name: 'Owens', email: 'rita.owens@example.com',   founder_count: 2 },
      ],
      pending_applications: [
        { id: 21, first_name: 'Omar',   last_name: 'Shah',    email: 'omar.shah@startup.io',   applied_at: '2026-05-27' },
        { id: 22, first_name: 'Fatima', last_name: 'Osei',    email: 'fatima.osei@venture.co', applied_at: '2026-05-28' },
      ],
    };
  }
  const { data } = await api.get('/ceg/overview');
  return data;
}

export async function downloadProgramReport() {
  const resp = await fetch('/api/vr/ceg/program-report', { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'Overall Progress.zip';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function downloadFounderExport(founderUserId, company, lastName) {
  const resp = await fetch(`/api/vr/ceg/founders/${founderUserId}/export`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeCo = (company || lastName || 'Founder').replace(/\s+/g, '_');
  a.download = `${safeCo}_${lastName || 'Founder'}_Progress.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Admin ─────────────────────────────────────────────────────────────────────

/**
 * Download a CSV snapshot of all platform data for the cohort.
 * Date-stamped to match the server's Content-Disposition filename.
 */
export async function downloadPlatformData() {
  const resp = await fetch('/api/vr/reports/platform-export', { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  const blob = await resp.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `VRP_Platform_Data_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── HubSpot sync (admin) ──────────────────────────────────────────────────
// Preview is always safe — it writes nothing. Only confirmHubSpotSync writes,
// and the backend refuses unless HUBSPOT_SYNC_ENABLED=true.

export async function getHubSpotStatus() {
  const { data } = await api.get('/hubspot/status');
  return data;
}

export async function previewHubSpotSync() {
  const { data } = await api.post('/hubspot/preview');
  return data;
}

export async function confirmHubSpotSync(selections = null) {
  try {
    const body = selections ? { selections } : {};
    const { data } = await api.post('/hubspot/sync', body);
    return data;
  } catch (err) {
    // 409 carries an actionable configuration message; surface it verbatim.
    const detail = err.response?.data?.detail;
    throw new Error(detail || 'Sync failed. Please try again.');
  }
}

export async function getAdminApplications() {
  if (USE_MOCK) {
    await delay(400);
    return [
      { id: 10, email: 'jane@startup.com', first_name: 'Jane', last_name: 'Doe', status: 'pending' },
      { id: 11, email: 'bob@venture.co', first_name: 'Bob', last_name: 'Smith', status: 'pending' },
    ];
  }
  const { data } = await api.get('/admin/applications');
  return data;
}

export async function approveApplication(userId, eirId = null) {
  if (USE_MOCK) { await delay(400); return {}; }
  const { data } = await api.post(`/admin/applications/${userId}/approve`, { eir_id: eirId });
  return data;
}

export async function assignEIR(userId, eirId) {
  if (USE_MOCK) { await delay(300); return {}; }
  const { data } = await api.patch(`/admin/users/${userId}/assign-eir`, { eir_id: eirId });
  return data;
}

export async function getEIRList() {
  if (USE_MOCK) {
    await delay(300);
    return [
      { id: 2, first_name: 'Jane', last_name: 'Smith', email: 'eir@test.com', founder_count: 1 },
    ];
  }
  const { data } = await api.get('/admin/eirs');
  return data;
}

export async function getEIRFounders(eirId) {
  if (USE_MOCK) { await delay(300); return []; }
  const { data } = await api.get(`/admin/eirs/${eirId}/founders`);
  return data;
}

export async function rejectApplication(userId) {
  if (USE_MOCK) { await delay(400); return {}; }
  const { data } = await api.post(`/admin/applications/${userId}/reject`);
  return data;
}

export async function getAllUsers() {
  if (USE_MOCK) {
    await delay(400);
    return [
      { id: 1, email: 'founder@test.com', first_name: 'John',   last_name: 'Smith', role: 'founder', status: 'approved' },
      { id: 2, email: 'eir@test.com',     first_name: 'Jane',   last_name: 'Smith', role: 'eir',     status: 'approved' },
      { id: 3, email: 'ceg@test.com',     first_name: 'Marcus', last_name: 'Webb',  role: 'ceg',     status: 'approved' },
      { id: 4, email: 'admin@test.com',   first_name: 'Admin',  last_name: 'User',  role: 'admin',   status: 'approved' },
    ];
  }
  const { data } = await api.get('/admin/users');
  return data;
}

export async function getUserDetail(userId) {
  if (USE_MOCK) {
    await delay(300);
    return null;
  }
  const { data } = await api.get(`/admin/users/${userId}`);
  return data;
}

export async function createStaffUser(form) {
  if (USE_MOCK) {
    await delay(600);
    return { id: 99, ...form, status: 'approved' };
  }
  try {
    const { data } = await api.post('/admin/users', form);
    return data;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    if (err.response?.status === 400 && detail.toLowerCase().includes('already')) {
      throw new Error('An account with this email already exists.');
    }
    throw new Error(detail || 'Something went wrong. Please try again.');
  }
}

// ─── Account ───────────────────────────────────────────────────────────────────

export async function getAccount() {
  if (USE_MOCK) { await delay(300); return null; }
  const { data } = await api.get('/account');
  return data;
}

export async function updateAccountPersonal(fields) {
  if (USE_MOCK) { await delay(300); return fields; }
  const { data } = await api.patch('/account/personal', fields);
  return data;
}

export async function updateAccountProfile(fields) {
  if (USE_MOCK) { await delay(300); return {}; }
  const { data } = await api.patch('/account/profile', fields);
  return data;
}

export async function getMyEIRs() {
  if (USE_MOCK) { await delay(300); return []; }
  const { data } = await api.get('/account/my-eirs');
  return data;
}

export async function requestPasswordReset(email) {
  if (USE_MOCK) { await delay(400); return { message: 'Mock: reset link sent' }; }
  const { data } = await api.post('/auth/forgot-password', { email });
  return data;
}

export async function resetPassword(token, newPassword) {
  if (USE_MOCK) { await delay(400); return { message: 'Mock: password updated' }; }
  const { data } = await api.post('/auth/reset-password', {
    token,
    new_password: newPassword,
  });
  return data;
}

export async function changePassword(currentPassword, newPassword) {
  if (USE_MOCK) { await delay(400); return {}; }
  const { data } = await api.post('/account/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return data;
}

export async function uploadAvatar(file) {
  if (USE_MOCK) { await delay(500); return { avatar_url: null }; }
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post('/account/avatar', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ─── EIR ───────────────────────────────────────────────────────────────────────

export async function getMyFounders(scope = 'mine') {
  if (USE_MOCK) { await delay(400); return []; }
  const { data } = await api.get('/eir/founders', { params: { scope } });
  return data;
}

export async function getFounderDataRoom(founderUserId) {
  if (USE_MOCK) { await delay(400); return null; }
  const { data } = await api.get(`/eir/founders/${founderUserId}/data-room`);
  return data;
}

export async function getFounderFundraising(founderUserId) {
  if (USE_MOCK) { await delay(400); return null; }
  try {
    const { data } = await api.get(`/eir/founders/${founderUserId}/fundraising`);
    return data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

// ─── Fundraising Plan ──────────────────────────────────────────────────────────

export async function getFundraisingPlan() {
  if (USE_MOCK) { await delay(300); return null; }
  try {
    const { data } = await api.get('/fundraising');
    return data;
  } catch (err) {
    if (err.response?.status === 404) return null;
    throw err;
  }
}

export async function saveFundraisingPlan(answers) {
  if (USE_MOCK) { await delay(500); return answers; }
  const { data } = await api.post('/fundraising', answers);
  return data;
}

// ─── CRM ───────────────────────────────────────────────────────────────────────

export async function getCRM() {
  if (USE_MOCK) { await delay(400); return { contacts: [], assigned_eir: null }; }
  const { data } = await api.get('/crm');
  return data;
}

export async function createContact(body) {
  if (USE_MOCK) { await delay(400); return { id: Date.now(), ...body, note_count: 0 }; }
  const { data } = await api.post('/crm', body);
  return data;
}

export async function updateContact(id, body) {
  if (USE_MOCK) { await delay(300); return body; }
  const { data } = await api.patch(`/crm/${id}`, body);
  return data;
}

export async function deleteContact(id) {
  if (USE_MOCK) { await delay(300); return; }
  await api.delete(`/crm/${id}`);
}

export async function getContactNotes(contactId) {
  if (USE_MOCK) { await delay(300); return []; }
  const { data } = await api.get(`/crm/${contactId}/notes`);
  return data;
}

export async function addContactNote(contactId, body) {
  if (USE_MOCK) { await delay(300); return { id: Date.now(), body, created_at: new Date().toISOString() }; }
  const { data } = await api.post(`/crm/${contactId}/notes`, { body });
  return data;
}

export async function getFounderCRM(founderUserId) {
  if (USE_MOCK) { await delay(400); return { founder: {}, contacts: [] }; }
  const { data } = await api.get(`/eir/founders/${founderUserId}/crm`);
  return data;
}

export async function setDataRoomDeadline(founderUserId, categoryId, dueDate) {
  if (USE_MOCK) { await delay(300); return {}; }
  const { data } = await api.put(`/eir/founders/${founderUserId}/data-room/deadlines/${categoryId}`, { due_date: dueDate });
  return data;
}

export async function setDataRoomItemReview(founderUserId, itemId, status, note) {
  if (USE_MOCK) { await delay(200); return { item_id: itemId, review: status === 'none' ? null : { status, note } }; }
  const { data } = await api.put(`/eir/founders/${founderUserId}/data-room/items/${itemId}/review`, { status, note: note || null });
  return data;
}

export async function setContactFollowUp(founderUserId, contactId, followUpDate) {
  if (USE_MOCK) { await delay(300); return {}; }
  const { data } = await api.patch(`/eir/founders/${founderUserId}/crm/${contactId}/follow-up`, { next_follow_up_date: followUpDate || null });
  return data;
}

// ─── Connections (EIR outreach tracker) ─────────────────────────────────────────

export async function listConnections(params = {}) {
  if (USE_MOCK) { await delay(300); return { connections: [] }; }
  const { data } = await api.get('/connections', { params });
  return data;
}

export async function createConnection(body) {
  if (USE_MOCK) { await delay(300); return { id: Date.now(), ...body, note_count: 0 }; }
  const { data } = await api.post('/connections', body);
  return data;
}

export async function updateConnection(id, body) {
  if (USE_MOCK) { await delay(300); return body; }
  const { data } = await api.patch(`/connections/${id}`, body);
  return data;
}

export async function deleteConnection(id) {
  if (USE_MOCK) { await delay(300); return; }
  await api.delete(`/connections/${id}`);
}

export async function getConnectionNotes(id) {
  if (USE_MOCK) { await delay(300); return []; }
  const { data } = await api.get(`/connections/${id}/notes`);
  return data;
}

export async function addConnectionNote(id, body) {
  if (USE_MOCK) { await delay(300); return { id: Date.now(), body, created_at: new Date().toISOString() }; }
  const { data } = await api.post(`/connections/${id}/notes`, { body });
  return data;
}

export async function inviteConnection(id) {
  if (USE_MOCK) { await delay(300); return { id, status: 'invited' }; }
  const { data } = await api.post(`/connections/${id}/invite`);
  return data;
}

// ─── Data Room ─────────────────────────────────────────────────────────────────

export async function getDataRoom() {
  if (USE_MOCK) {
    await delay();
    return { ...MOCK_DATA_ROOM };
  }
  const { data } = await api.get('/data-room');
  return data;
}

export async function uploadDataRoomItem(itemId, file) {
  if (USE_MOCK) {
    await delay(600);
    const blobUrl = URL.createObjectURL(file);
    const now = new Date().toISOString().slice(0, 10);
    return { url: blobUrl, name: file.name, uploadedAt: now };
  }
  try {
    const form = new FormData();
    form.append('file', file);
    const { data } = await api.post(`/data-room/items/${itemId}/upload`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    if (!err.response) throw new Error('Unable to reach the server. Check your connection and try again.');
    throw new Error(detail || 'Upload failed. Please try again.');
  }
}

export async function getItemVersions(itemId) {
  if (USE_MOCK) {
    await delay();
    for (const cat of MOCK_DATA_ROOM.categories) {
      const item = cat.items.find((i) => i.id === itemId);
      if (item) return item.versions;
    }
    return [];
  }
  const { data } = await api.get(`/data-room/items/${itemId}/versions`);
  return data;
}

export async function getEIRNotes(founderUserId) {
  if (USE_MOCK) { await delay(300); return []; }
  const { data } = await api.get(`/eir/founders/${founderUserId}/notes`);
  return data;
}

export async function createEIRNote(founderUserId, body, page, notify) {
  if (USE_MOCK) { await delay(400); return { id: 1, body, page, notify, created_at: new Date().toISOString().split('T')[0], eir_first_name: 'John', eir_last_name: 'Smith' }; }
  const { data } = await api.post(`/eir/founders/${founderUserId}/notes`, { body, page, notify });
  return data;
}

export async function getMyEIRNotes() {
  if (USE_MOCK) { await delay(300); return { notes: [], last_seen_at: null }; }
  const { data } = await api.get('/data-room/notes');
  return data;
}

export async function markNotesAsSeen() {
  if (USE_MOCK) { await delay(100); return {}; }
  const { data } = await api.post('/data-room/notes/mark-seen');
  return data;
}

export async function notifyEIROfUpload() {
  if (USE_MOCK) { await delay(300); return { message: 'EIR notified' }; }
  const { data } = await api.post('/data-room/notify-eir');
  return data;
}

export async function exportDataRoom() {
  const resp = await fetch('/api/vr/data-room/export', { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  return resp.blob();
}

export async function downloadBlankTemplate() {
  const resp = await fetch('/api/vr/data-room/template', { credentials: 'include' });
  if (!resp.ok) throw new Error('Template download failed');
  return resp.blob();
}

export async function exportFounderCategory(founderUserId, categoryId) {
  const resp = await fetch(`/api/vr/eir/founders/${founderUserId}/data-room/export/${categoryId}`, { credentials: 'include' });
  if (!resp.ok) throw new Error('Export failed');
  return resp.blob();
}

// Creates a staff account. Omit `password` and the server mints the temporary
// one, returning it as temp_password on this response only — it is not stored
// in plaintext, so a lost one has to be reissued via adminResetPassword().
export async function createUser(payload) {
  if (USE_MOCK) {
    await delay(400);
    return { id: 99, ...payload, status: 'approved', temp_password: 'MockTemp1!x' };
  }
  const { data } = await api.post('/admin/users', payload);
  return data;
}

export async function deleteUser(userId) {
  if (USE_MOCK) {
    await delay(400);
    return { message: 'User deleted' };
  }
  const { data } = await api.delete(`/admin/users/${userId}`);
  return data;
}

export async function banUser(userId) {
  if (USE_MOCK) { await delay(300); return { message: 'User banned' }; }
  const { data } = await api.post(`/admin/users/${userId}/ban`);
  return data;
}

export async function unbanUser(userId) {
  if (USE_MOCK) { await delay(300); return { message: 'User unbanned' }; }
  const { data } = await api.post(`/admin/users/${userId}/unban`);
  return data;
}

export async function adminResetPassword(userId) {
  const { data } = await api.post(`/admin/users/${userId}/reset-password`);
  return data;
}

export async function eirResetPassword(founderUserId) {
  const { data } = await api.post(`/eir/founders/${founderUserId}/reset-password`);
  return data;
}

// ─── Files ─────────────────────────────────────────────────────────────────────

export async function getMyFiles() {
  const { data } = await api.get('/files');
  return data;
}

export async function uploadVRCA(file) {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await api.post('/files/vrca', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    if (!err.response) throw new Error('Unable to reach the server. Check your connection and try again.');
    throw new Error(detail || 'Upload failed. Please try again.');
  }
}

export async function uploadGeneralFile(file) {
  const form = new FormData();
  form.append('file', file);
  try {
    const { data } = await api.post('/files/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  } catch (err) {
    const detail = err.response?.data?.detail || '';
    if (!err.response) throw new Error('Unable to reach the server. Check your connection and try again.');
    throw new Error(detail || 'Upload failed. Please try again.');
  }
}

export async function deleteGeneralFile(fileId) {
  await api.delete(`/files/${fileId}`);
}

export async function getFounderFiles(founderUserId) {
  const { data } = await api.get(`/eir/founders/${founderUserId}/files`);
  return data;
}

// ─── EIR: Engagement Status ────────────────────────────────────────────────

export async function setFounderEngagementStatus(founderUserId, engagementStatus) {
  const { data } = await api.patch(`/eir/founders/${founderUserId}/engagement-status`, { engagement_status: engagementStatus });
  return data;
}

// ─── Admin: Engagement Status ───────────────────────────────────────────────

export async function setEngagementStatus(userId, engagementStatus) {
  const { data } = await api.patch(`/admin/users/${userId}/engagement-status`, { engagement_status: engagementStatus });
  return data;
}

// ─── Admin: Secondary EIRs ─────────────────────────────────────────────────

export async function getSecondaryEIRs(userId) {
  const { data } = await api.get(`/admin/users/${userId}/secondary-eirs`);
  return data;
}

export async function addSecondaryEIR(userId, eirId) {
  const { data } = await api.post(`/admin/users/${userId}/secondary-eirs`, { eir_id: eirId });
  return data;
}

export async function removeSecondaryEIR(userId, eirId) {
  await api.delete(`/admin/users/${userId}/secondary-eirs/${eirId}`);
}

// ─── CEG Funding Applications — PARKED ────────────────────────────────────
//
// The /funding router is no longer mounted in main.py, so every call here would
// 404. Kept for a one-line revert alongside the router mount.
//
// export async function submitFundingApplication(body) {
//   const { data } = await api.post('/funding/apply', body);
//   return data;
// }
//
// export async function getMyFundingApplications() {
//   const { data } = await api.get('/funding/my-applications');
//   return data;
// }
//
// export async function getAdminFundingApplications() {
//   const { data } = await api.get('/funding/admin/applications');
//   return data;
// }
//
// export async function reviewFundingApplication(id, body) {
//   const { data } = await api.patch(`/funding/admin/applications/${id}`, body);
//   return data;
// }

// ─── CRM: CSV Export / Import ──────────────────────────────────────────────

export async function exportContacts() {
  const response = await fetch('/api/vr/crm/export', { credentials: 'include' });
  if (!response.ok) throw new Error('Export failed');
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'investor-contacts.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importContacts(file) {
  const form = new FormData();
  form.append('file', file);
  const { data } = await api.post('/crm/import', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  return data;
}

